import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IconButton, makeStyles } from '@material-ui/core';
import { DeleteForever, ExpandLess, ExpandMore } from '@material-ui/icons';

import { deleteFormReg } from '../../../actions/publishActions';
import ConfirmationDialog from '../../../reusable/ConfirmationDialog/ConfirmationDialog';

import styles from './Conversions.module.scss';

const useStyles = makeStyles({
    button: {
        color: 'red',
        marginTop: '10px',
        paddingLeft: 0,
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:focus': {
            backgroundColor: 'transparent',
            outline: 'none',
        },
    },
    icon: {
        fontSize: 20,
        marginRight: 5,
    },
    text: {
        marginTop: '2px',
        fontSize: '12px',
    },
});

interface ConversionItemProps {
    formId: string;
    conversion: any;
    orderedFields: string[];
    onChange: (_id: string) => void;
};

const ConversionItem = ({ formId, conversion, orderedFields, onChange }: ConversionItemProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch() as any;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { defaultAccountId } = useSelector((state: any) => state.session);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const deleteBtnClasses = useStyles();

    // find any props that have been removed from the form when it has been editted
    const missingFields = useMemo(() => {
        const orderedFieldsSet = new Set(orderedFields);
        if (!(conversion.fields ?? {})) {
            return [];
        }
        return Object.keys((conversion.fields ?? {})).filter(str => !orderedFieldsSet.has(str));
    }, [orderedFields])

    const renderConversion = (key: string) => {
        if (!conversion.fields || !conversion.fields[key]) {
            return (
                <div key={key} style={{ color: 'gray' }}>
                    <div className={styles.label}>{key}</div>
                    <div className={styles.value}>{'\u00A0'}</div>
                </div>
            )
        };
        const field = (conversion.fields ?? {})[key];
        return field && (
            <div key={key} style={{ color: missingFields.includes(key) ? 'gray' : 'inherit' }}>
                <div className={styles.label}>{field.label}</div>
                <div className={styles.value}>{field.value || '\u00A0'}</div>
            </div>
        )
    };

    const removeRegistrant = (choice: { ok: boolean }) => {
        if (choice.ok) {
            dispatch(deleteFormReg(defaultAccountId, formId, conversion.id)).then(() => {
                onChange(conversion.id);
            });
        }
        setShowConfirmationDialog(false);
    };

    if (!conversion.fields) {
        return null;
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.conversion}>
                    <div className={styles.name}>
                        {(conversion.fields ?? {}).firstName?.value} {(conversion.fields ?? {}).lastName?.value}
                    </div>
                    <div className={styles.date}>
                        {t('COMMON_REGISTERED')} {moment(conversion.createdAt).format('YYYY-MM-DD HH:mm')}
                    </div>
                </div>
                <div className={styles.iconBtn}>
                    <IconButton onClick={() => { setIsOpen(prev => !prev) }}>
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </div>
            </div>
            {isOpen &&
                <div className={styles.listContainer}>
                    {[...orderedFields, ...missingFields].map((key: string) => (
                        renderConversion(key)
                    ))}
                    <IconButton onClick={() => setShowConfirmationDialog(true)} className={deleteBtnClasses.button}>
                        <DeleteForever className={deleteBtnClasses.icon} />
                        <span className={deleteBtnClasses.text}>{t('INSPECT_MEDIA_MINI_FORM_REMOVE_REGISTRANT')}</span>
                    </IconButton>
                    {showConfirmationDialog && (
                        <ConfirmationDialog
                            title={t('INSPECT_MEDIA_MINI_FORM_REMOVE_REGISTRANT')}
                            callback={removeRegistrant}
                            message={t('INSPECT_MEDIA_MINI_FORM_REMOVE_CONFIRM_MSG')}
                            okButtonText={t('COMMON_YES')}
                            cancelButtonText={t('COMMON_CANCEL')}
                        />
                    )}
                </div>
            }
        </>
    );
};

export default ConversionItem;
